import { Col, DatePicker, Form, Select } from 'antd';
import { BaseButton } from 'components/button/button-base';
import dayjs from 'dayjs';
import { dateFormat } from 'lib/constants/date-time';
import {
  PatientInjuryKey,
  PatientProfile2,
  PatientProfileKey
} from 'lib/documents';
import { handleFormChange, selectProps } from 'lib/utils/antd';
import { clear } from 'lib/utils/object';
import { omit } from 'lodash';
import { LanguageSelect } from './components/LanguageSelect';
import { ProfileFormProps } from './ProfileForm';
import { keyCondition, keyDependency } from './utils/key-dependency';
import { englishFluencyOptions, genderOptions } from './utils/select-options';

/**
 * Form that patients complete to create their ConcussionRx profile
 */
export function ProfileForm2(
  props: ProfileFormProps & { value: PatientProfile2; submitLoading?: boolean }
) {
  const { value: formValue, onChange } = props;
  const questions: { component: () => JSX.Element }[] = [
    {
      component: () => (
        <Form.Item
          hasFeedback
          className="form_item"
          name={PatientProfileKey.birthDate}
          label="Date of Birth"
          rules={[
            {
              required: true,
              message: 'Date of Birth is required'
            }
          ]}
        >
          <DatePicker
            format={dateFormat}
            maxDate={dayjs(new Date()).subtract(1, 'month')}
            placeholder="Date of Birth"
          />
        </Form.Item>
      )
    },
    {
      component: () => (
        <Form.Item
          hasFeedback
          validateFirst
          className="form_item"
          label="Sex at Birth"
          name={PatientProfileKey.sex}
          rules={[
            {
              required: true,
              message: 'Sex at birth is required'
            }
          ]}
        >
          <Select
            labelInValue
            options={genderOptions}
            {...selectProps}
            placeholder="Sex at Birth"
          />
        </Form.Item>
      )
    },
    {
      component: () => (
        <Form.Item
          hasFeedback
          className="form_item"
          name={PatientProfileKey.primaryLanguage}
          label="Primary Language"
          rules={[
            {
              required: true,
              message: 'Primary Language is required'
            }
          ]}
        >
          <LanguageSelect {...selectProps} placeholder="Primary Language" />
        </Form.Item>
      )
    },
    {
      component: () => (
        <Form.Item
          hasFeedback
          className="form_item"
          name={PatientProfileKey.englishFluency}
          label="English Fluency"
          rules={[
            {
              required: true,
              message: 'English fluency is required'
            }
          ]}
        >
          <Select
            options={englishFluencyOptions}
            {...selectProps}
            placeholder="English Fluency"
          />
        </Form.Item>
      )
    }
  ];

  const [form] = Form.useForm();
  return (
    <Form
      form={form}
      className="profile-form"
      name="patient_profile"
      layout="vertical"
      onValuesChange={(...args) =>
        handleValuesChange(...args, formValue, onChange)
      }
      initialValues={{ ...formValue }}
      onFinish={props.onDone}
      labelCol={{ span: 12 }}
      style={{
        width: '100%',
        display: 'flex',
        flexDirection: 'row',
        justifyContent: 'center'
      }}
    >
      <Col sm={24} md={12} lg={12}>
        {questions.map((val, index) => (
          <val.component key={index} />
        ))}

        {props.onDone && (
          <BaseButton
            style={{ marginTop: '40px' }}
            type="primary"
            htmlType="submit"
            loading={props.submitLoading}
          >
            Next
          </BaseButton>
        )}
      </Col>
    </Form>
  );
}

/**
 * Handle form change events
 * @param changedValue A single key-value pair
 * @param changedValues All the key-value pairs in the form
 * @returns
 */
function handleValuesChange(
  changedValue: Record<string, any>,
  changedValues: Record<string, any>,
  formValue: PatientProfile2 | undefined,
  onChange: (val: PatientProfile2) => void | any
) {
  let newValue: Partial<PatientProfile2> | undefined;

  // Get the name of the changed value
  const key = Object.keys(changedValue)[0];
  if (key === 'image') return;

  // The birthdate is changed
  if (key === PatientProfileKey.birthDate) {
    // WARN: If changedValue is in changedValues then is this always true?
    if (changedValues[key]) {
      const birthDate = changedValues[key].startOf('day').toDate();
      newValue = { ...formValue, [key]: birthDate };
    } else {
      clear(changedValues, [key]);
      newValue = omit(formValue, [key]);
    }
  }

  // The injury date is changed
  // TODO: Refactor this to keep birthDate and injuryDate logic DRY
  if (key === PatientInjuryKey.injuryDate) {
    if (changedValues[key]) {
      const injuryDate = changedValues[key].startOf('day').toDate();
      newValue = { ...formValue, [key]: injuryDate };
    } else {
      clear(changedValues, [key]);
      newValue = omit(formValue, [key]);
    }
  }

  // WARN: Unreachable because of an earlier exit when key === "image"
  if (key === 'image') {
    /**
     * check if firebase storage already has profile image for user
     * overwrite it if it exists
     * we upload to firebase storage
     * update auth user profileUrl to point at firebase storage
     * we don't have to save it into collections.
     */
    return;
  }
  onChange(
    newValue
      ? newValue
      : handleFormChange(
          changedValue,
          changedValues,
          formValue,
          keyDependency,
          keyCondition
        )
  );
}
